import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { CommonCloseIcon } from '@local/assets/icons'

import Clickable from '@web/components/Clickable'
import ModalContainer from '@web/components/Modal/ModalContainer'
import { ModalComponent } from '@web/components/Modal/types'
import { useModal } from '@web/components/Modal/useModal'
import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { useGoTo } from '@web/hooks/useGoTo'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import routes from '@web/libs/constants/routes'
import nClicks from '@web/libs/nlog/click/codes'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'

import Alert from '../Alert'
import styles from './index.module.scss'
import Policy from './Policy'

const ReceiptParticipation_SignUpMutation = gql(`
  mutation ReceiptParticipation_SignUp {
    signup(isReceiptParticipant: true) {
      ... on SignUp_Succeed {
        __typename
        user {
          id
          isParticipant
        }
      }
      ... on BaseError {
        __typename
        message
      }
    }
  }
`)

interface Props {
  onSignUp?: () => void
}

const ReceiptParticipation: ModalComponent<Props> = ({ showModal, cancelModal, confirmModal, onSignUp }) => {
  const [personalInfoAgree, setPersonalInfoAgree] = useState(false)
  const { goTo } = useGoTo()

  const [signup] = useMutation(ReceiptParticipation_SignUpMutation)

  const { openModal: openLoadingModal, closeModal: closeLoadingModal } = useModal(Alert, {
    hasLoading: true,
    message: '리뷰어 자격 검토중입니다.',
  })

  const handleAfterSignUp = () => {
    if (onSignUp) {
      onSignUp()
      return
    }

    goTo(routes.HOME)
  }

  const handleError = (message: string) => {
    openErrorToast(message)
    neloLogger('error', `participate ${extractErrorMessage(message)}`)
  }

  const participate = async () => {
    openLoadingModal()
    const { data, errors: networkError } = await signup()
    closeLoadingModal()

    const { SignUp_Succeed, AuthenticationError, NotFoundError, ForbiddenError, UnknownError } = extractByTypename(
      data?.signup,
    )

    const error = AuthenticationError ?? NotFoundError ?? ForbiddenError ?? UnknownError

    if (SignUp_Succeed) {
      confirmModal()
      handleAfterSignUp()
      return
    }

    if (error) {
      handleError(error.message)
    }

    if (networkError) {
      const message = '네트워크 연결 상태를 확인해 주세요.'
      handleError(message)
    }

    cancelModal()
  }

  const handleClick = () => {
    participate()
  }

  return (
    <ModalContainer showModal={showModal} className={styles.modal_application}>
      <Policy agree={personalInfoAgree} onChangeAgree={agree => setPersonalInfoAgree(agree)} />
      <div className={styles.btn_area}>
        <Clickable
          className={styles.btn}
          disabled={!personalInfoAgree}
          nClickCode={nClicks.apply.START}
          onClick={handleClick}
        >
          영수증 인증 리뷰하기
        </Clickable>
      </div>
      <Clickable className={styles.btn_close} nClickCode={nClicks.apply.CLOSE} onClick={cancelModal}>
        <CommonCloseIcon className={styles.icon} aria-hidden="true" />
        <span className="place_blind">닫기</span>
      </Clickable>
    </ModalContainer>
  )
}

export default ReceiptParticipation
